// Shared database-import pipeline.
//
// Mirrors the create logic in containers/SideNav/SidenavContent.jsx so the Home
// "Import JSON" button can reuse the exact same behaviour. NOTE: this app mutates
// the redux `database.list` / `database.groups` arrays in place and refreshes
// views via the database EventBus, so these functions mutate the arrays passed in
// `ctx` and the caller is responsible for emitting the `changed` event afterwards.
//
// TODO(follow-up): migrate SidenavContent's two import callers to use this module
// to remove the remaining duplication (left untouched here to avoid regressing the
// existing, separately-verified sidebar import flow).
import Helpers from '../../ts/utils/Helpers';

function checkDatabaseForAgainModify(databaseData) {
    const originalDatabase = {};
    const foreignKeyFields = Helpers.getForeignKeyFieldsFromSchema(databaseData.schema);
    if (foreignKeyFields.length > 0) {
        originalDatabase[databaseData.code] = {
            isDraft: databaseData.isDraft,
            foreignKeyFields: Helpers.deepCopy(foreignKeyFields)
        };
        databaseData.isDraft = true;
    }
    for (const field of foreignKeyFields) {
        delete field.schema.foreignKeys;
    }
    return originalDatabase;
}

async function createDatabase(data, {registryService, databaseList, authUser}) {
    // NOTE: unlike the original SidenavContent copy, failures are NOT swallowed
    // here — they propagate so the caller can report the real error instead of a
    // misleading "import successful" toast.
    data['$$mode'] = 'new';
    const createdDatabaseResponse = await registryService.modifyDatabase(data);
    const databaseResponse = await registryService.getDatabase(createdDatabaseResponse.data.id);
    const databaseCatalog = {
        id: createdDatabaseResponse.data.catalog_id,
        name: data.catalog_name,
        code: createdDatabaseResponse.data.catalog_code,
        group_id: createdDatabaseResponse.data.group_id,
        data_index_increment: createdDatabaseResponse.data.data_index_increment,
        databases: []
    };
    databaseResponse.data.catalog = databaseCatalog;
    databaseCatalog.databases.push(databaseResponse.data);
    Helpers.assignDatabaseCatalogPermission(authUser, databaseCatalog);
    databaseList.push(databaseCatalog);
    return {id: databaseResponse.data.id, isDraft: !!databaseResponse.data.is_draft};
}

async function modifyDatabaseForOriginalData(originalDatabase, {registryService, databaseList}) {
    if (Object.keys(originalDatabase).length === 0) {
        return;
    }
    for (const catalogCode of Object.keys(originalDatabase)) {
        const databaseCatalog = databaseList.find(c => c.code === catalogCode);
        if (databaseCatalog && databaseCatalog.databases.length === 1 && databaseCatalog.databases[0].is_draft) {
            const database = databaseCatalog.databases[0];
            const databaseData = {
                id: database.id,
                isDraft: originalDatabase[catalogCode].isDraft,
                schema: Helpers.deepCopy(database.schema),
                code: catalogCode,
                catalog_name: databaseCatalog.name
            };
            const fields = Helpers.getFieldsFromSchema(databaseData.schema);
            for (const fkField of originalDatabase[catalogCode].foreignKeyFields) {
                const field = fields.find(f => f.schema['$id'] === fkField.schema['$id']);
                if (!field) {
                    continue;
                }
                const foreignKeys = [];
                for (const foreignKey of fkField.schema.foreignKeys) {
                    const relatedCatalog = databaseList.find(c => c.code === foreignKey.databaseKey);
                    if (relatedCatalog) {
                        foreignKey.databaseKey = String(relatedCatalog.id);
                        foreignKeys.push(foreignKey);
                    }
                }
                if (foreignKeys.length > 0) {
                    field.schema.foreignKeys = foreignKeys;
                }
            }
            await registryService.modifyDatabase(databaseData);
            const databaseResponse = await registryService.getDatabase(databaseData.id);
            databaseCatalog.databases[0] = databaseResponse.data;
            databaseCatalog.databases[0].catalog = databaseCatalog;
        }
    }
}

// Decode a FileReader data-URL into the parsed JSON object, or null on failure.
//
// The export writes plain UTF-8 JSON, so we base64-decode to bytes and run them
// through TextDecoder. (The legacy `decodeURIComponent(atob(...))` used elsewhere
// THROWS on any literal `%` in the file and mangles non-ASCII text — accents,
// etc. — so it is deliberately not used here.) The caller decides whether the
// parsed object is a full database/group export or a bare schema.
// One-shot hand-off of an imported schema to the create page. The app uses a
// hash router, which does NOT preserve `location.state`, so we stash the schema
// here and the create page takes it once on mount.
let pendingImportSchema = null;

export function setPendingImportSchema(schema) {
    pendingImportSchema = schema;
}

export function takePendingImportSchema() {
    const schema = pendingImportSchema;
    pendingImportSchema = null;
    return schema;
}

export function decodeImportFile(dataUrl) {
    try {
        const content = dataUrl.split(',')[1];
        const binary = atob(content);
        const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
        return JSON.parse(new TextDecoder('utf-8').decode(bytes));
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[import] failed to parse import file', e);
        return null;
    }
}

// Run the full import pipeline. `ctx` = {registryService, databaseList,
// databaseGroups, authUser, onGroupUpdated?}. Mutates databaseList/databaseGroups
// in place (the app's refresh convention).
export async function runDatabaseImport(data, ctx) {
    const {registryService, databaseGroups, onGroupUpdated} = ctx;
    let originalDatabase = {};
    const created = [];
    if (Array.isArray(data.groups)) {
        for (const group of data.groups) {
            const oldGroup = databaseGroups.find(g => g.name === group.name);
            if (oldGroup) {
                group.id = oldGroup.id;
            }
            // The backend requires `color_hex` (api/views.py modify_database_catalog_group),
            // but group exports omit it. Reuse an existing group's colour or fall back to
            // the default so importing a grouped export doesn't fail with "color_hex is required".
            if (!group.color_hex) {
                group.color_hex = (oldGroup && oldGroup.color_hex) || '#316896';
            }
            const response = await registryService.modifyDatabaseGroup(group);
            if (response.status === 201) {
                databaseGroups.push(response.data);
            } else {
                const index = databaseGroups.findIndex(g => g.id === response.data.id);
                if (index >= 0) {
                    databaseGroups[index] = response.data;
                }
            }
            group.id = response.data.id;
            if (onGroupUpdated) {
                onGroupUpdated();
            }
            if (!group.id || !Array.isArray(group.databases)) {
                continue;
            }
            for (const databaseData of group.databases) {
                databaseData.group_id = group.id;
                originalDatabase = {...originalDatabase, ...checkDatabaseForAgainModify(databaseData)};
                const result = await createDatabase(databaseData, ctx);
                if (result) {
                    created.push(result);
                }
            }
        }
    }
    if (Array.isArray(data.databases)) {
        for (const databaseData of data.databases) {
            originalDatabase = {...originalDatabase, ...checkDatabaseForAgainModify(databaseData)};
            const result = await createDatabase(databaseData, ctx);
            if (result) {
                created.push(result);
            }
        }
    }
    await modifyDatabaseForOriginalData(originalDatabase, ctx);
    return created;
}
